import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Paperclip, Upload, Download, Trash2, FileText, FileSpreadsheet, FileImage } from "lucide-react";
import { toast } from "sonner";
import { hasRole } from "@/lib/roles";

const ICONS_BY_EXT = {
  pdf: FileText, doc: FileText, docx: FileText, txt: FileText, csv: FileSpreadsheet,
  xls: FileSpreadsheet, xlsx: FileSpreadsheet,
  png: FileImage, jpg: FileImage, jpeg: FileImage,
};
const ALLOWED_EXTS = ["pdf", "doc", "docx", "xls", "xlsx", "png", "jpg", "jpeg", "txt", "csv", "zip"];
const MAX_BYTES = 10 * 1024 * 1024;

const ext = (name) => (name?.includes(".") ? name.split(".").pop().toLowerCase() : "");
const humanSize = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export function EvidenceUploader({ assessmentId, indicatorCode, locked = false }) {
  const { user } = useAuth();
  const baseCanWrite = hasRole(user, "valuatore", "cliente");
  const canWrite = baseCanWrite && !locked;
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const uploadingRef = useRef(false); // not reactive, just prevents double-submit

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/assessments/${assessmentId}/indicators/${indicatorCode}/evidence`
      );
      setFiles(data || []);
    } catch (e) {
      // silently ignore
    }
  }, [assessmentId, indicatorCode]);

  useEffect(() => { load(); }, [load]);

  const upload = useCallback(async (file) => {
    if (!file || uploadingRef.current) return;
    if (!ALLOWED_EXTS.includes(ext(file.name))) {
      toast.error(`Tipo file non ammesso. Estensioni: ${ALLOWED_EXTS.join(", ")}`);
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("File troppo grande (max 10 MB)");
      return;
    }
    uploadingRef.current = true;
    // Single-file policy: delete any existing evidence first.
    try {
      for (const old of files) {
        await api.delete(`/evidence/${old.id}`).catch(() => {});
      }
    } catch (e) { /* best-effort */ }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(
        `/assessments/${assessmentId}/indicators/${indicatorCode}/evidence`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success(`"${file.name}" caricato`);
      window.dispatchEvent(new CustomEvent("evidence:updated"));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally {
      uploadingRef.current = false;
    }
  }, [assessmentId, indicatorCode, load, files]);

  const download = useCallback(async (f) => {
    try {
      const res = await api.get(`/evidence/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = f.filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Errore download");
    }
  }, []);

  const remove = useCallback(async (f) => {
    if (!window.confirm(`Eliminare "${f.filename}"?`)) return;
    try {
      await api.delete(`/evidence/${f.id}`);
      toast.success("File eliminato");
      window.dispatchEvent(new CustomEvent("evidence:updated"));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore eliminazione");
    }
  }, [load]);

  const updateMeta = useCallback(async (f, patch) => {
    try {
      await api.patch(`/evidence/${f.id}`, patch);
      // Optimistic local update so the input doesn't flicker
      setFiles((prev) => prev.map((x) => (x.id === f.id ? { ...x, ...patch } : x)));
      window.dispatchEvent(new CustomEvent("evidence:updated"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore aggiornamento metadati");
    }
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  }, [upload]);

  return (
    <div className="mt-3" data-testid={`evidence-block-${indicatorCode}`}>
      {canWrite && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed cursor-pointer transition text-xs
            ${dragOver ? "border-viva-600 bg-viva-50 text-viva-800"
                       : "border-slate-300 text-slate-500 hover:border-viva-400 hover:bg-slate-50"}`}
          data-testid={`evidence-dropzone-${indicatorCode}`}
        >
          <Upload className="w-3.5 h-3.5" /> {files.length > 0
            ? "Sostituisci evidenza (PDF, DOC, XLS, PNG, JPG, ZIP · max 10 MB)"
            : "Carica evidenza (PDF, DOC, XLS, PNG, JPG, ZIP · max 10 MB · 1 file — usa ZIP per multipli)"}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.csv,.zip"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
            data-testid={`evidence-input-${indicatorCode}`}
          />
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-2 space-y-2" data-testid={`evidence-list-${indicatorCode}`}>
          {files.map((f) => {
            const Icon = ICONS_BY_EXT[ext(f.filename)] || Paperclip;
            return (
              <li key={f.id} className="rounded bg-slate-50 border border-slate-200 text-xs">
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <Icon className="w-3.5 h-3.5 text-viva-600 shrink-0" />
                  {f.evidence_code && (
                    <code
                      className="px-1.5 py-0.5 rounded bg-viva-100 text-viva-800 font-mono text-[10px] font-bold shrink-0"
                      data-testid={`evidence-code-${f.id}`}
                      title="Codice univoco evidenza (per assessment + pillar)"
                    >
                      {f.evidence_code}
                    </code>
                  )}
                  <span className="truncate flex-1 text-slate-700" title={f.filename}>{f.filename}</span>
                  <span className="text-slate-400 text-[10px] shrink-0">{humanSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => download(f)}
                    className="p-1 rounded hover:bg-viva-100 text-viva-700"
                    title="Scarica"
                    data-testid={`evidence-download-${f.id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => remove(f)}
                      className="p-1 rounded hover:bg-red-100 text-red-600"
                      title="Elimina"
                      data-testid={`evidence-delete-${f.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="px-2 pb-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <label className="flex flex-col gap-0.5 sm:col-span-2">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Nome documento</span>
                    <input
                      type="text"
                      defaultValue={f.doc_name || ""}
                      placeholder={canWrite ? "Es. Politica ambientale 2025" : "—"}
                      disabled={!canWrite}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v !== (f.doc_name || "")) updateMeta(f, { doc_name: v });
                      }}
                      data-testid={`evidence-docname-${f.id}`}
                      className="h-7 px-2 rounded border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-viva-500/30 focus:border-viva-500 disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </label>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Data documento</span>
                    <input
                      type="date"
                      defaultValue={f.doc_date || ""}
                      disabled={!canWrite}
                      onBlur={(e) => {
                        const v = e.target.value;
                        if (v !== (f.doc_date || "")) updateMeta(f, { doc_date: v });
                      }}
                      data-testid={`evidence-docdate-${f.id}`}
                      className="h-7 px-2 rounded border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-viva-500/30 focus:border-viva-500 disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {!canWrite && files.length === 0 && (
        <p className="text-[11px] text-slate-400 italic">Nessuna evidenza caricata</p>
      )}
    </div>
  );
}
